import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useTabParam } from "../hooks/useTabParam";
import { useFeatures } from "../context/FeaturesContext";

import { ApiError, api } from "../api/client";
import BottomNav from "../components/BottomNav";
import PasskeyPromptModal from "../components/PasskeyPromptModal";
import { useAuth } from "../context/AuthContext";
import { useDeviceFingerprint } from "../hooks/useDeviceFingerprint";
import { applyLanguage } from "../i18n";
import DropdownSelect from "../components/DropdownSelect";
import type {
  ChangeRequest,
  ChangeRequestStatus,
  DriversLicense,
  Education,
  EmployeeAsset,
  EquipmentRequisition,
  LeaveEntitlement,
  LeaveRequest,
  Me,
  Passkey,
  PreviousWorkExperience,
  ProfileField,
  VisaRightToWork,
} from "../types";
import {
  formatAuthMethod,
  formatEmploymentType,
  formatFieldValue,
  formatLeaveStatus,
  formatRequisitionStatus,
  formatRole,
  formatVisaStatus,
  leaveStatusTone,
  requisitionStatusTone,
  visaStatusTone,
} from "../utils/formatters";
import {
  guessDeviceName,
  passkeyAvailable,
  registerPasskey,
} from "../utils/webauthn";
import {
  Banknote,
  Briefcase,
  Building2,
  CalendarHeart,
  Check,
  FileText,
  GraduationCap,
  HeartHandshake,
  IdCard,
  KeyRound,
  Landmark,
  Languages,
  LogOut,
  Mail,
  MapPin,
  Palmtree,
  Phone,
  SlidersHorizontal,
  Smartphone,
  TrendingUp,
  UserRound,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import PlaceholderPortrait from "../components/brand/PlaceholderPortrait";

const DATE_FMT = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });
const DATETIME_FMT = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

const FIELD_GROUP_ORDER = [
  "Personal",
  "Address",
  "Contact",
  "Emergency",
  "Employment",
];

const GROUP_ICONS: Record<string, LucideIcon> = {
  personal: UserRound,
  address: MapPin,
  contact: Phone,
  emergency: HeartHandshake,
  employment: Briefcase,
  "bank details": Landmark,
  bank: Landmark,
  compensation: Banknote,
  system: SlidersHorizontal,
};

/** A small picture for a field, chosen from its name. Falls back to the group's. */
function fieldIcon(fieldName: string, group: string): LucideIcon {
  const n = fieldName.toLowerCase();
  if (n.includes("email")) return Mail;
  if (n.includes("phone") || n.includes("mobile")) return Phone;
  if (n.includes("birth") || n.includes("date")) return CalendarHeart;
  if (n.includes("language")) return Languages;
  if (n.includes("passport") || n.includes("number") || n.includes("nin")) return IdCard;
  if (n.includes("address") || n.includes("city") || n.includes("post") || n.includes("country"))
    return MapPin;
  if (n.includes("site") || n.includes("department")) return Building2;
  if (n.includes("manager") || n.includes("relationship")) return UsersRound;
  return GROUP_ICONS[group.toLowerCase()] ?? UserRound;
}

const ME_TABS = ["profile", "documents", "timeoff", "training", "performance"] as const;
type TabId = (typeof ME_TABS)[number];

function calculateTenure(startDate: string | undefined | null): string {
  if (!startDate) return "";
  const start = new Date(startDate);
  if (isNaN(start.getTime())) return "";
  const now = new Date();
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  let days = now.getDate() - start.getDate();
  if (days < 0) {
    months -= 1;
    const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    days += prevMonth.getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years < 0) return "";
  if (years >= 1) return `${years}y · ${months}m · ${days}d`;
  return `${months}m · ${days}d`;
}

function changeRequestStatusTone(status: ChangeRequestStatus): string {
  switch (status) {
    case "pending":
      return "bg-amber-50 text-amber-800";
    case "approved":
      return "bg-green-50 text-green-700";
    case "rejected":
      return "bg-red-50 text-red-700";
    case "cancelled":
      return "bg-gray-100 text-gray-500";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ProfileScreen() {
  const { t } = useTranslation();
  const { employee, logout, refresh, isAdmin, isKiosk } = useAuth();
  const deviceFingerprint = useDeviceFingerprint();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useTabParam(ME_TABS, "profile");
  const trainingOn = useFeatures().isEnabled("training_enabled");
  // One profile section is shown at a time so the page fits on one screen.
  const [section, setSection] = useState<string | null>(null);

  // PIN state
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [pinError, setPinError] = useState<string | null>(null);
  const [pinSuccess, setPinSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [bindNotice, setBindNotice] = useState<string | null>(null);
  const [binding, setBinding] = useState(false);
  const [language, setLanguage] = useState<string>("en-GB");
  const [languageSaved, setLanguageSaved] = useState<string | null>(null);

  // Passkeys
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [platformPasskey, setPlatformPasskey] = useState<boolean | null>(null);
  const [showPasskeyPrompt, setShowPasskeyPrompt] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);

  // Self-service profile
  const [profileFields, setProfileFields] = useState<ProfileField[]>([]);
  const [profileLoading, setProfileLoading] = useState(true);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [savingFields, setSavingFields] = useState<Record<string, boolean>>({});
  const [savedFields, setSavedFields] = useState<Record<string, boolean>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>(
    {},
  );

  // Change requests
  const [changeRequestField, setChangeRequestField] = useState<string | null>(
    null,
  );
  const [changeRequestValue, setChangeRequestValue] = useState("");
  const [changeRequestReason, setChangeRequestReason] = useState("");
  const [changeRequestSubmitting, setChangeRequestSubmitting] = useState(false);
  const [pendingFields, setPendingFields] = useState<Set<string>>(new Set());
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);
  const [changeRequestsLoading, setChangeRequestsLoading] = useState(true);
  const [changeRequestsOpen, setChangeRequestsOpen] = useState(false);
  const [cancellingId, setCancellingId] = useState<number | null>(null);

  const loadPasskeys = useCallback(async () => {
    try {
      const list = await api.get<Passkey[]>("/api/auth/passkey");
      setPasskeys(list);
    } catch {
      setPasskeys([]);
    }
  }, []);

  const loadProfile = useCallback(async () => {
    setProfileLoading(true);
    try {
      const fields = await api.get<ProfileField[]>(
        "/api/self-service/profile",
      );
      setProfileFields(fields);
      const initial: Record<string, string> = {};
      for (const f of fields) {
        if (f.access_level === "direct_edit") {
          initial[f.field_name] = f.current_value ?? "";
        }
      }
      setEditValues(initial);
    } catch {
      setProfileFields([]);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  const loadChangeRequests = useCallback(async () => {
    setChangeRequestsLoading(true);
    try {
      const reqs = await api.get<ChangeRequest[]>(
        "/api/self-service/change-requests",
      );
      setChangeRequests(reqs);
      const pending = new Set<string>();
      for (const r of reqs) {
        if (r.status === "pending") pending.add(r.field_name);
      }
      setPendingFields(pending);
    } catch {
      setChangeRequests([]);
    } finally {
      setChangeRequestsLoading(false);
    }
  }, []);

  useEffect(() => {
    void passkeyAvailable().then(setPlatformPasskey);
    void loadPasskeys();
    void loadProfile();
    void loadChangeRequests();
  }, [loadPasskeys, loadProfile, loadChangeRequests]);

  useEffect(() => {
    if (!employee || isKiosk) return;
    if (employee.must_change_pin) return;
    if (platformPasskey !== true) return;
    if (passkeys.length > 0) return;
    if (employee.auth_method === "passkey" || employee.auth_method === "both") {
      setShowPasskeyPrompt(true);
    }
  }, [employee, isKiosk, passkeys.length, platformPasskey]);

  useEffect(() => {
    if (employee?.preferred_language) setLanguage(employee.preferred_language);
  }, [employee?.preferred_language]);

  if (!employee) return null;

  const isDeviceBound =
    !!employee.device_fingerprint &&
    employee.device_fingerprint === deviceFingerprint;
  const pinAvailable =
    employee.auth_method === "pin" || employee.auth_method === "both";

  // --- handlers ---------------------------------------------------------------

  const handleDirectEditSave = async (groupFields: ProfileField[]) => {
    const directs = groupFields.filter(
      (f) => f.access_level === "direct_edit",
    );
    if (directs.length === 0) return;

    const payload: Record<string, string> = {};
    let hasChanges = false;
    for (const f of directs) {
      const newVal = editValues[f.field_name] ?? "";
      if (newVal !== (f.current_value ?? "")) {
        payload[f.field_name] = newVal;
        hasChanges = true;
      }
    }
    if (!hasChanges) return;

    const savingUpdate: Record<string, boolean> = {};
    for (const f of directs) savingUpdate[f.field_name] = true;
    setSavingFields((prev) => ({ ...prev, ...savingUpdate }));

    try {
      await api.put("/api/self-service/profile", { updates: payload });
      setProfileFields((prev) =>
        prev.map((f) =>
          payload[f.field_name] !== undefined
            ? { ...f, current_value: payload[f.field_name] }
            : f,
        ),
      );
      const savedUpdate: Record<string, boolean> = {};
      const errUpdate: Record<string, string | null> = {};
      for (const f of directs) {
        savedUpdate[f.field_name] = true;
        errUpdate[f.field_name] = null;
      }
      setSavedFields((prev) => ({ ...prev, ...savedUpdate }));
      setFieldErrors((prev) => ({ ...prev, ...errUpdate }));
      setTimeout(() => {
        setSavedFields((prev) => {
          const next = { ...prev };
          for (const f of directs) delete next[f.field_name];
          return next;
        });
      }, 3000);
    } catch (err) {
      const errUpdate: Record<string, string | null> = {};
      const msg =
        err instanceof ApiError ? err.message : "Failed to save changes";
      for (const f of directs) errUpdate[f.field_name] = msg;
      setFieldErrors((prev) => ({ ...prev, ...errUpdate }));
    } finally {
      const clearSaving: Record<string, boolean> = {};
      for (const f of directs) clearSaving[f.field_name] = false;
      setSavingFields((prev) => ({ ...prev, ...clearSaving }));
    }
  };

  const handleChangeRequestSubmit = async (field: ProfileField) => {
    if (!changeRequestValue.trim()) return;
    setChangeRequestSubmitting(true);
    try {
      await api.post("/api/self-service/change-request", {
        field_name: field.field_name,
        requested_value: changeRequestValue,
        reason: changeRequestReason || null,
      });
      setPendingFields((prev) => new Set(prev).add(field.field_name));
      setChangeRequestField(null);
      setChangeRequestValue("");
      setChangeRequestReason("");
      await loadChangeRequests();
    } catch (err) {
      setFieldErrors((prev) => ({
        ...prev,
        [field.field_name]:
          err instanceof ApiError ? err.message : "Failed to submit request",
      }));
    } finally {
      setChangeRequestSubmitting(false);
    }
  };

  const handleCancelChangeRequest = async (id: number) => {
    setCancellingId(id);
    try {
      await api.delete(`/api/self-service/change-requests/${id}`);
      await loadChangeRequests();
    } catch {
      /* silently fail */
    } finally {
      setCancellingId(null);
    }
  };

  const submitPin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPinError(null);
    setPinSuccess(null);
    if (newPin !== confirmPin) {
      setPinError(t("auth.pinMismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await api.put("/api/auth/change-pin", {
        current_pin: currentPin,
        new_pin: newPin,
      });
      setPinSuccess(t("auth.pinChanged"));
      setCurrentPin("");
      setNewPin("");
      setConfirmPin("");
      await refresh();
      if (employee.must_change_pin) {
        navigate(isAdmin ? "/admin" : "/home", { replace: true });
      }
    } catch (err) {
      setPinError(err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const bindDevice = async () => {
    setBindNotice(null);
    setBinding(true);
    try {
      await api.post("/api/auth/bind-device", {
        device_fingerprint: deviceFingerprint,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      await refresh();
      setBindNotice(t("auth.device.boundSuccess"));
    } catch (err) {
      setBindNotice(
        err instanceof ApiError ? err.message : t("auth.device.bindFailed"),
      );
    } finally {
      setBinding(false);
    }
  };

  const saveLanguage = async (value: string) => {
    setLanguage(value);
    setLanguageSaved(null);
    applyLanguage(value);
    try {
      await api.put("/api/auth/preferences", { preferred_language: value });
      await refresh();
      setLanguageSaved(t("profile.languageUpdated"));
    } catch (err) {
      console.warn("language persistence failed", err);
    }
  };

  const addPasskey = async () => {
    setPasskeyError(null);
    try {
      await registerPasskey(guessDeviceName());
      await loadPasskeys();
    } catch (err) {
      setPasskeyError((err as Error).message || t("common.error"));
    }
  };

  const removePasskey = async (passkeyId: number) => {
    if (!confirm(t("common.confirm") + "?")) return;
    try {
      await api.delete(`/api/auth/passkey/${passkeyId}`);
      await loadPasskeys();
      await refresh();
    } catch (err) {
      setPasskeyError(err instanceof ApiError ? err.message : t("common.error"));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const bindingStatus = employee.device_fingerprint
    ? isDeviceBound
      ? t("auth.device.bound")
      : t("auth.device.boundToDifferent")
    : t("auth.device.notBound");

  // Group profile fields
  const groupedFields: Record<string, ProfileField[]> = {};
  for (const f of profileFields) {
    if (!groupedFields[f.field_group]) groupedFields[f.field_group] = [];
    groupedFields[f.field_group].push(f);
  }
  const sortedGroups = Object.keys(groupedFields).sort((a, b) => {
    const ia = FIELD_GROUP_ORDER.indexOf(a);
    const ib = FIELD_GROUP_ORDER.indexOf(b);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });

  // --- Profile tab content ----------------------------------------------------
  const sections: { id: string; label: string; icon: LucideIcon }[] = [
    ...sortedGroups.map((g) => ({
      id: g,
      label: t(`selfService.fieldGroups.${g.toLowerCase()}`, g),
      icon: GROUP_ICONS[g.toLowerCase()] ?? UserRound,
    })),
    { id: "security", label: t("profile.security"), icon: KeyRound },
    { id: "preferences", label: t("profile.preferences"), icon: SlidersHorizontal },
  ];
  const activeSection =
    section && sections.some((x) => x.id === section) ? section : sections[0].id;

  const profileTabContent = (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {sections.map(({ id, label, icon: Icon }) => {
          const on = id === activeSection;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setSection(id)}
              aria-pressed={on}
              className={[
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition",
                on
                  ? "border-pme-blue bg-pme-blue text-white"
                  : "border-pme-line bg-white text-pme-navy hover:bg-pme-sky/50",
              ].join(" ")}
            >
              <Icon size={14} strokeWidth={2} aria-hidden />
              {label}
            </button>
          );
        })}
      </div>
      {employee.must_change_pin && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
          {t("profile.mustChangePin")}
        </div>
      )}

      {/* Active Surveys */}
      <ActiveSurveysSection />

      {/* Self-service fields */}
      {!profileLoading && profileFields.length > 0 && (
        <>
          {sortedGroups.filter((g) => g === activeSection).map((group) => {
            const fields = groupedFields[group];
            const GroupIcon = GROUP_ICONS[group.toLowerCase()] ?? UserRound;
            const hasDirectEdits = fields.some(
              (f) => f.access_level === "direct_edit",
            );
            const groupHasErrors = fields.some(
              (f) => fieldErrors[f.field_name],
            );
            const groupIsSaving = fields.some(
              (f) => savingFields[f.field_name],
            );
            const groupHasSaved = fields.some(
              (f) => savedFields[f.field_name],
            );

            return (
              <section key={group} className="card space-y-4 p-5">
                <SectionTitle icon={GroupIcon}>
                  {t(`selfService.fieldGroups.${group.toLowerCase()}`, group)}
                </SectionTitle>
                <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
                  {fields.map((field) => (
                    <ProfileFieldRow
                      key={field.field_name}
                      field={field}
                      editValue={editValues[field.field_name] ?? ""}
                      onEditChange={(val) =>
                        setEditValues((prev) => ({
                          ...prev,
                          [field.field_name]: val,
                        }))
                      }
                      isSaving={!!savingFields[field.field_name]}
                      isSaved={!!savedFields[field.field_name]}
                      error={fieldErrors[field.field_name] ?? null}
                      isPending={pendingFields.has(field.field_name)}
                      isChangeRequestOpen={
                        changeRequestField === field.field_name
                      }
                      onOpenChangeRequest={() => {
                        setChangeRequestField(field.field_name);
                        setChangeRequestValue("");
                        setChangeRequestReason("");
                      }}
                      onCloseChangeRequest={() => setChangeRequestField(null)}
                      changeRequestValue={changeRequestValue}
                      onChangeRequestValueChange={setChangeRequestValue}
                      changeRequestReason={changeRequestReason}
                      onChangeRequestReasonChange={setChangeRequestReason}
                      changeRequestSubmitting={changeRequestSubmitting}
                      onSubmitChangeRequest={() =>
                        handleChangeRequestSubmit(field)
                      }
                    />
                  ))}
                </div>
                {hasDirectEdits && (
                  <div className="flex items-center gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => handleDirectEditSave(fields)}
                      disabled={groupIsSaving}
                      className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                    >
                      {groupIsSaving ? t("auth.saving") : t("common.save")}
                    </button>
                    {groupHasSaved && (
                      <span className="text-xs text-green-600">
                        {t("common.save")}d
                      </span>
                    )}
                    {groupHasErrors && (
                      <span className="text-xs text-red-600">
                        {fields
                          .map((f) => fieldErrors[f.field_name])
                          .find(Boolean)}
                      </span>
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </>
      )}

      {profileLoading && (
        <div className="card p-5">
          <div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
          <div className="mt-4 space-y-3">
            <div className="h-4 w-full animate-pulse rounded bg-gray-100" />
            <div className="h-4 w-3/4 animate-pulse rounded bg-gray-100" />
          </div>
        </div>
      )}

      {/* My Change Requests */}
      {!changeRequestsLoading && changeRequests.length > 0 && (
        <ChangeRequestsSection
          changeRequests={changeRequests}
          changeRequestsOpen={changeRequestsOpen}
          onToggle={() => setChangeRequestsOpen((prev) => !prev)}
          cancellingId={cancellingId}
          onCancel={handleCancelChangeRequest}
        />
      )}

      {/* Security */}
      {activeSection === "security" && (
      <section className="card space-y-4 p-5">
        <SectionTitle icon={KeyRound}>{t("profile.security")}</SectionTitle>
        <div className="text-sm">
          <p className="text-gray-800">
            {t("profile.authMethod")}:{" "}
            <span className="font-semibold">
              {formatAuthMethod(employee.auth_method)}
            </span>
          </p>
          <div className="mt-3">
            <p className="text-xs font-medium text-gray-700">
              {t("auth.passkey.registeredPasskeys")}
            </p>
            {passkeys.length === 0 && (
              <p className="mt-1 text-xs text-gray-400">
                {t("auth.passkey.noneRegistered")}
              </p>
            )}
            <ul className="mt-2 space-y-2">
              {passkeys.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {p.device_name}
                    </p>
                    <p className="text-[11px] text-gray-500">
                      {DATE_FMT.format(new Date(p.created_at))}
                      {p.last_used_at &&
                        ` · ${DATETIME_FMT.format(new Date(p.last_used_at))}`}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removePasskey(p.id)}
                    className="rounded-lg border border-red-200 px-2 py-1 text-xs text-red-600"
                  >
                    {t("auth.passkey.remove")}
                  </button>
                </li>
              ))}
            </ul>
            {platformPasskey && (
              <button
                type="button"
                onClick={addPasskey}
                className="mt-3 rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                {t("auth.passkey.registerNew")}
              </button>
            )}
            {platformPasskey === false && (
              <p className="mt-2 text-xs text-gray-500">
                {t("auth.passkey.notAvailable")}
              </p>
            )}
            {passkeyError && (
              <p className="mt-2 text-xs text-red-600">{passkeyError}</p>
            )}
          </div>

          {employee.is_admin && (
            <AdminPinSection employee={employee} />
          )}
        </div>
      </section>
      )}

      {/* Language & Device */}
      {activeSection === "preferences" && (
      <section className="card space-y-4 p-5">
        <SectionTitle icon={SlidersHorizontal}>{t("profile.preferences")}</SectionTitle>
        <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-gray-600">
            {t("profile.language")}
          </label>
          <DropdownSelect
            listCode="language"
            value={language}
            onChange={(v) => void saveLanguage(v)}
            className="input mt-1"
          />
          {languageSaved && (
            <p className="mt-1 text-xs text-green-600">{languageSaved}</p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">
            {t("auth.device.section")}
          </label>
          <p className="mt-1 text-sm text-gray-800">{bindingStatus}</p>
          {!isDeviceBound && (
            <button
              type="button"
              onClick={bindDevice}
              disabled={binding}
              className="mt-2 rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {binding ? t("auth.device.binding") : t("auth.device.bindThis")}
            </button>
          )}
          {bindNotice && (
            <p className="mt-1 text-xs text-gray-600">{bindNotice}</p>
          )}
        </div>
        </div>
      </section>
      )}

      {/* Change PIN */}
      {activeSection === "security" && pinAvailable && (
        <section className="card space-y-4 p-5">
          <SectionTitle icon={KeyRound}>{t("profile.changePinTitle")}</SectionTitle>
          <form onSubmit={submitPin} className="grid gap-3 sm:grid-cols-3">
            <PinField
              id="current_pin"
              label={t("auth.currentPin")}
              value={currentPin}
              onChange={setCurrentPin}
              autoComplete="current-password"
            />
            <PinField
              id="new_pin"
              label={t("auth.newPin")}
              value={newPin}
              onChange={setNewPin}
              autoComplete="new-password"
            />
            <PinField
              id="confirm_pin"
              label={t("auth.confirmPin")}
              value={confirmPin}
              onChange={setConfirmPin}
              autoComplete="new-password"
            />
            <div className="flex flex-wrap items-center gap-3 sm:col-span-3">
              <button type="submit" disabled={submitting} className="btn-primary">
                {submitting ? t("auth.saving") : t("profile.updatePin")}
              </button>
              {pinError && <p className="text-sm text-red-600">{pinError}</p>}
              {pinSuccess && (
                <p className="text-sm text-green-600">{pinSuccess}</p>
              )}
            </div>
          </form>
        </section>
      )}

    </div>
  );

  // --- Render -----------------------------------------------------------------

  const tenure = calculateTenure(employee.start_date);
  const startDateFmt = new Date(employee.start_date).toLocaleDateString(
    "en-GB",
    { year: "numeric", month: "short", day: "numeric" },
  );

  return (
    <div className="flex min-h-dvh flex-col bg-pme-paper safe-x">
      <div className="flex-1 overflow-y-auto pb-24">
        <div className="mx-auto max-w-5xl px-5 safe-top">
          <div className="card flex flex-wrap items-center gap-4 p-4">
            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full border-2 border-white shadow-card ring-1 ring-pme-line">
              {employee.profile_photo_path ? (
                <img
                  src={employee.profile_photo_path}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <PlaceholderPortrait title={employee.name} className="h-full w-full" />
              )}
            </div>
            {/* min width so the name never collapses on a phone; the badge
                and Sign out wrap to the next line instead. */}
            <div className="min-w-[10rem] flex-1">
              <h1 className="truncate text-xl font-bold text-pme-navy">{employee.name}</h1>
              {employee.job_title && (
                <p className="truncate text-sm font-bold text-pme-blue">{employee.job_title}</p>
              )}
              <p className="mt-0.5 truncate text-xs text-pme-muted">
                {[employee.employee_number, employee.site_name, employee.department_name]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700 ring-1 ring-green-200">
              <Check size={12} strokeWidth={2.5} aria-hidden />
              {t("common.active")}
            </span>
            <button type="button" onClick={handleLogout} className="btn-quiet">
              <LogOut size={15} strokeWidth={2} aria-hidden />
              {t("auth.logout")}
            </button>
          </div>
        </div>

        <div className="mx-auto max-w-5xl px-5 pt-4">
          <div className="flex gap-5">
            {/* Vitals sidebar (desktop) */}
            <aside className="hidden w-[230px] shrink-0 lg:block">
              <div className="card sticky top-4 p-4">
                <h3 className="eyebrow">{t("employeeDetails.vitals")}</h3>
                <ul className="mt-3 space-y-2.5 text-sm text-pme-ink">
                  <VitalRow icon={Mail} breakAll>{employee.email}</VitalRow>
                  {employee.phone && <VitalRow icon={Smartphone}>{employee.phone}</VitalRow>}
                  <VitalRow icon={MapPin}>{employee.site_name}</VitalRow>
                  {employee.department_name && (
                    <VitalRow icon={Building2}>{employee.department_name}</VitalRow>
                  )}
                  <VitalRow icon={Briefcase}>
                    {formatEmploymentType(employee.employment_type)}
                  </VitalRow>
                  <VitalRow icon={IdCard}>{formatRole(employee.role)}</VitalRow>
                  <VitalRow icon={CalendarHeart}>
                    {startDateFmt}
                    {tenure && <span className="block text-xs text-pme-muted">{tenure}</span>}
                  </VitalRow>
                  <VitalRow icon={UsersRound}>
                    {employee.manager_name ?? t("employeeDetails.noManager")}
                  </VitalRow>
                </ul>
              </div>
            </aside>

            {/* Main content */}
            <div className="min-w-0 flex-1 space-y-4">
              <TabBar activeTab={activeTab} onChange={setActiveTab} />

              {activeTab === "profile" && profileTabContent}
              {activeTab === "documents" && (
                <DocumentsTab employeeId={employee.employee_id} />
              )}
              {activeTab === "timeoff" && (
                <TimeOffTab employeeId={employee.employee_id} />
              )}
              {activeTab === "training" && trainingOn && (
                <TrainingTab employeeId={employee.employee_id} />
              )}
              {activeTab === "performance" && (
                <PerformanceTab employeeId={employee.employee_id} />
              )}
            </div>
          </div>
        </div>
      </div>

      {showPasskeyPrompt && (
        <PasskeyPromptModal
          onDone={() => {
            setShowPasskeyPrompt(false);
            void loadPasskeys();
          }}
          onSkip={() => setShowPasskeyPrompt(false)}
        />
      )}

      <BottomNav />
    </div>
  );
}

// ---------------------------------------------------------------------------
// TabBar
// ---------------------------------------------------------------------------

function TabBar({
  activeTab,
  onChange,
}: {
  activeTab: TabId;
  onChange: (id: TabId) => void;
}) {
  const { t } = useTranslation();
  const { isEnabled } = useFeatures();
  const tabs: { id: TabId; label: string; icon: LucideIcon }[] = [
    { id: "profile", label: t("employeeTabs.profile"), icon: UserRound },
    { id: "documents", label: t("employeeTabs.documents"), icon: FileText },
    { id: "timeoff", label: t("employeeTabs.timeOff"), icon: Palmtree },
    ...(isEnabled("training_enabled")
      ? [{ id: "training" as TabId, label: t("employeeTabs.training"), icon: GraduationCap }]
      : []),
    { id: "performance", label: t("employeeTabs.performance"), icon: TrendingUp },
  ];
  return (
    <nav className="card flex flex-wrap gap-1 p-1">
      {tabs.map(({ id, label, icon: Icon }) => {
        const active = id === activeTab;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            aria-current={active ? "page" : undefined}
            className={[
              "inline-flex shrink-0 items-center gap-1.5 rounded-xl px-3 py-2 text-sm transition",
              active
                ? "bg-pme-sky font-semibold text-pme-navy"
                : "text-pme-muted hover:bg-pme-paper hover:text-pme-navy",
            ].join(" ")}
          >
            <Icon size={16} strokeWidth={active ? 2 : 1.75} aria-hidden />
            {label}
          </button>
        );
      })}
    </nav>
  );
}

function SectionTitle({ icon: Icon, children }: { icon: LucideIcon; children: React.ReactNode }) {
  return (
    <h2 className="flex items-center gap-2 text-sm font-bold text-pme-navy">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-pme-sky text-pme-blue">
        <Icon size={16} strokeWidth={2} aria-hidden />
      </span>
      {children}
    </h2>
  );
}

function VitalRow({
  icon: Icon,
  breakAll,
  children,
}: {
  icon: LucideIcon;
  breakAll?: boolean;
  children: React.ReactNode;
}) {
  return (
    <li className={["flex items-start gap-2", breakAll ? "break-all" : ""].join(" ")}>
      <Icon size={15} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-pme-blue" />
      <span>{children}</span>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Documents Tab (read-only: education, visa, license, work history)
// ---------------------------------------------------------------------------

interface MyDocument {
  id: number;
  employee_id: number | null;
  category_id: number;
  title: string;
  description: string | null;
  file_name: string;
  file_type: string;
  file_size_bytes: number;
  version: number;
  expires_at: string | null;
  requires_signature: boolean;
  signed_at: string | null;
  acknowledged_at: string | null;
  is_active: boolean;
  created_at: string;
}

interface DocCategory {
  id: number;
  name: string;
  description: string | null;
  requires_acknowledgement: boolean;
  is_system: boolean;
}

function DocumentsTab({ employeeId }: { employeeId: number }) {
  const { t } = useTranslation();
  const [education, setEducation] = useState<Education[]>([]);
  const [visas, setVisas] = useState<VisaRightToWork[]>([]);
  const [licenses, setLicenses] = useState<DriversLicense[]>([]);
  const [workHistory, setWorkHistory] = useState<PreviousWorkExperience[]>([]);
  const [loading, setLoading] = useState(true);

  // Policy documents state
  const [policyDocs, setPolicyDocs] = useState<MyDocument[]>([]);
  const [categories, setCategories] = useState<DocCategory[]>([]);
  const [acknowledgedIds, setAcknowledgedIds] = useState<Set<number>>(new Set());
  const [acknowledgingId, setAcknowledgingId] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([
      api.get<Education[]>(`/api/employees/${employeeId}/education`),
      api.get<VisaRightToWork[]>(`/api/employees/${employeeId}/visa`),
      api.get<DriversLicense[]>(`/api/employees/${employeeId}/drivers-license`),
      api.get<PreviousWorkExperience[]>(
        `/api/employees/${employeeId}/work-history`,
      ),
      api.get<MyDocument[]>("/api/documents/my"),
      api.get<DocCategory[]>("/api/documents/categories"),
    ])
      .then(([edu, vis, lic, wh, docs, cats]) => {
        if (edu.status === "fulfilled") setEducation(edu.value);
        if (vis.status === "fulfilled") setVisas(vis.value);
        if (lic.status === "fulfilled") setLicenses(lic.value);
        if (wh.status === "fulfilled") setWorkHistory(wh.value);
        if (docs.status === "fulfilled" && cats.status === "fulfilled") {
          setCategories(cats.value);
          const ackCatIds = new Set(
            cats.value
              .filter((c) => c.requires_acknowledgement)
              .map((c) => c.id),
          );
          setPolicyDocs(
            docs.value.filter((d) => ackCatIds.has(d.category_id)),
          );
          setAcknowledgedIds(
            new Set(
              docs.value
                .filter((d) => d.acknowledged_at !== null)
                .map((d) => d.id),
            ),
          );
        }
      })
      .finally(() => setLoading(false));
  }, [employeeId]);

  const handleAcknowledge = async (docId: number) => {
    setAcknowledgingId(docId);
    try {
      await api.post(`/api/documents/${docId}/acknowledge`);
      setAcknowledgedIds((prev) => new Set(prev).add(docId));
    } catch {
      /* silently fail */
    } finally {
      setAcknowledgingId(null);
    }
  };

  const getCategoryName = (categoryId: number): string => {
    const cat = categories.find((c) => c.id === categoryId);
    return cat?.name ?? "";
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Policy Documents requiring acknowledgement */}
      {policyDocs.length > 0 && (
        <section className="rounded-xl border border-gray-200 bg-white p-6">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            {t("employeeDetails.policyDocuments", "Policy Documents")}
          </h3>
          <div className="mt-3 space-y-3">
            {policyDocs.map((doc) => {
              const isAcknowledged = acknowledgedIds.has(doc.id);
              const isAcknowledging = acknowledgingId === doc.id;
              return (
                <div
                  key={doc.id}
                  className="rounded-lg border border-gray-100 bg-gray-50/50 p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {doc.title}
                      </p>
                      {doc.description && (
                        <p className="text-xs text-gray-500">{doc.description}</p>
                      )}
                      <p className="mt-1 text-[11px] text-gray-400">
                        {getCategoryName(doc.category_id)}
                        {doc.created_at && (
                          <> &middot; {DATE_FMT.format(new Date(doc.created_at))}</>
                        )}
                      </p>
                    </div>
                    {isAcknowledged ? (
                      <span className="flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700 ring-1 ring-green-200">
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        {t("documents.acknowledged", "Acknowledged")}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleAcknowledge(doc.id)}
                        disabled={isAcknowledging}
                        className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                      >
                        {isAcknowledging
                          ? t("auth.saving")
                          : t("documents.iAcknowledge", "I Acknowledge")}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Education */}
      <section className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t("employeeDetails.education")}
        </h3>
        {education.length === 0 ? (
          <p className="mt-3 text-sm text-gray-400">{t("common.noData")}</p>
        ) : (
          <div className="mt-3 space-y-3">
            {education.map((edu) => (
              <div
                key={edu.id}
                className="rounded-lg border border-gray-100 bg-gray-50/50 p-3"
              >
                <p className="text-sm font-medium text-gray-900">
                  {edu.institution}
                </p>
                {edu.degree && (
                  <p className="text-xs text-gray-600">{edu.degree}</p>
                )}
                {edu.major_specialisation && (
                  <p className="text-xs text-gray-500">
                    {edu.major_specialisation}
                  </p>
                )}
                {(edu.start_date || edu.end_date) && (
                  <p className="mt-1 text-[11px] text-gray-400">
                    {edu.start_date ? DATE_FMT.format(new Date(edu.start_date)) : ""}
                    {edu.start_date && edu.end_date ? " – " : ""}
                    {edu.end_date ? DATE_FMT.format(new Date(edu.end_date)) : ""}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Visa / Right to Work */}
      <section className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t("employeeDetails.visa")}
        </h3>
        {visas.length === 0 ? (
          <p className="mt-3 text-sm text-gray-400">{t("common.noData")}</p>
        ) : (
          <div className="mt-3 space-y-3">
            {visas.map((visa) => (
              <div
                key={visa.id}
                className="rounded-lg border border-gray-100 bg-gray-50/50 p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {visa.visa_type}
                    </p>
                    <p className="text-xs text-gray-500">
                      {visa.issuing_country}
                    </p>
                  </div>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${visaStatusTone(visa.status)}`}
                  >
                    {formatVisaStatus(visa.status)}
                  </span>
                </div>
                {(visa.issued_date || visa.expiry_date) && (
                  <p className="mt-1 text-[11px] text-gray-400">
                    {visa.issued_date
                      ? DATE_FMT.format(new Date(visa.issued_date))
                      : ""}
                    {visa.issued_date && visa.expiry_date ? " – " : ""}
                    {visa.expiry_date
                      ? DATE_FMT.format(new Date(visa.expiry_date))
                      : ""}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Drivers License */}
      <section className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t("employeeDetails.drivers")}
        </h3>
        {licenses.length === 0 ? (
          <p className="mt-3 text-sm text-gray-400">{t("common.noData")}</p>
        ) : (
          <div className="mt-3 space-y-3">
            {licenses.map((lic) => (
              <div
                key={lic.id}
                className="rounded-lg border border-gray-100 bg-gray-50/50 p-3"
              >
                <p className="text-sm font-medium text-gray-900">
                  {lic.license_number}
                </p>
                {lic.issuing_country && (
                  <p className="text-xs text-gray-500">
                    {lic.issuing_country}
                  </p>
                )}
                {lic.license_code && (
                  <p className="text-xs text-gray-500">
                    {t("employeeDetails.licenseCode")}: {lic.license_code}
                  </p>
                )}
                {(lic.issue_date || lic.expiry_date) && (
                  <p className="mt-1 text-[11px] text-gray-400">
                    {lic.issue_date
                      ? DATE_FMT.format(new Date(lic.issue_date))
                      : ""}
                    {lic.issue_date && lic.expiry_date ? " – " : ""}
                    {lic.expiry_date
                      ? DATE_FMT.format(new Date(lic.expiry_date))
                      : ""}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Previous Work Experience */}
      <section className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t("employeeDetails.workHistory")}
        </h3>
        {workHistory.length === 0 ? (
          <p className="mt-3 text-sm text-gray-400">{t("common.noData")}</p>
        ) : (
          <div className="mt-3 space-y-3">
            {workHistory.map((wh) => (
              <div
                key={wh.id}
                className="rounded-lg border border-gray-100 bg-gray-50/50 p-3"
              >
                <p className="text-sm font-medium text-gray-900">
                  {wh.company}
                </p>
                {wh.job_title && (
                  <p className="text-xs text-gray-600">{wh.job_title}</p>
                )}
                {(wh.start_date || wh.end_date) && (
                  <p className="mt-1 text-[11px] text-gray-400">
                    {wh.start_date
                      ? DATE_FMT.format(new Date(wh.start_date))
                      : ""}
                    {wh.start_date && wh.end_date ? " – " : ""}
                    {wh.end_date
                      ? DATE_FMT.format(new Date(wh.end_date))
                      : t("common.active")}
                  </p>
                )}
                {wh.responsibilities && (
                  <p className="mt-1 text-xs text-gray-500">
                    {wh.responsibilities}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Time Off Tab
// ---------------------------------------------------------------------------

function TimeOffTab({ employeeId }: { employeeId: number }) {
  const { t } = useTranslation();
  const [entitlements, setEntitlements] = useState<LeaveEntitlement[]>([]);
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const year = new Date().getFullYear();
    Promise.all([
      api.get<LeaveEntitlement[]>(
        `/api/leave/entitlements?employee_id=${employeeId}&year=${year}`,
      ),
      api.get<LeaveRequest[]>(
        `/api/leave/requests?employee_id=${employeeId}&limit=20`,
      ),
    ])
      .then(([ent, req]) => {
        setEntitlements(ent);
        setRequests(req);
      })
      .catch(() => {
        /* silently fail */
      })
      .finally(() => setLoading(false));
  }, [employeeId]);

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Balances */}
      <section className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t("leave.myBalance")}
        </h3>
        {entitlements.length === 0 ? (
          <p className="mt-3 text-sm text-gray-400">{t("common.noData")}</p>
        ) : (
          <div className="mt-3 space-y-2">
            {entitlements.map((ent) => {
              const used = parseFloat(ent.used_days);
              const total = parseFloat(ent.total_days);
              const remaining = parseFloat(ent.remaining_days);
              const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
              return (
                <div
                  key={ent.id}
                  className="rounded-lg border border-gray-100 p-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">
                      {ent.leave_type_name}
                    </span>
                    <span className="text-sm font-semibold text-gray-700">
                      {remaining} / {total} {t("leave.days")}
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-gray-100">
                    <div
                      className="h-1.5 rounded-full bg-pme-blue transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Recent requests */}
      <section className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t("leave.myRequests")}
        </h3>
        {requests.length === 0 ? (
          <p className="mt-3 text-sm text-gray-400">{t("leave.noRequests")}</p>
        ) : (
          <div className="mt-3 space-y-2">
            {requests.map((req) => (
              <div
                key={req.id}
                className="flex items-center justify-between rounded-lg border border-gray-100 p-3"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {req.leave_type_name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {DATE_FMT.format(new Date(req.start_date))} –{" "}
                    {DATE_FMT.format(new Date(req.end_date))}
                    <span className="ml-2 text-gray-400">
                      ({req.total_days} {t("leave.days")})
                    </span>
                  </p>
                </div>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${leaveStatusTone(req.status)}`}
                >
                  {formatLeaveStatus(req.status)}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Training Tab
// ---------------------------------------------------------------------------

interface TrainingAssignment {
  id: number;
  course_id: number;
  course_title: string | null;
  status: string;
  assigned_at: string;
  started_at: string | null;
  completed_at: string | null;
  expires_at: string | null;
  score: number | null;
}

function trainingStatusTone(status: string): string {
  switch (status) {
    case "completed":
      return "bg-green-50 text-green-700";
    case "in_progress":
      return "bg-blue-50 text-blue-700";
    case "not_started":
      return "bg-gray-100 text-gray-600";
    case "expired":
      return "bg-red-50 text-red-700";
    case "failed":
      return "bg-red-50 text-red-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

function TrainingTab({ employeeId: _employeeId }: { employeeId: number }) {
  const { t } = useTranslation();
  const [trainings, setTrainings] = useState<TrainingAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<number | null>(null);

  const loadTrainings = useCallback(async () => {
    try {
      const data = await api.get<TrainingAssignment[]>("/api/training/my");
      setTrainings(data);
    } catch {
      setTrainings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTrainings();
  }, [loadTrainings]);

  const handleStart = async (id: number) => {
    setActionId(id);
    try {
      await api.post(`/api/training/${id}/start`, {});
      await loadTrainings();
    } catch {
      /* silently fail */
    } finally {
      setActionId(null);
    }
  };

  const handleComplete = async (id: number) => {
    setActionId(id);
    try {
      await api.post(`/api/training/${id}/complete`, {});
      await loadTrainings();
    } catch {
      /* silently fail */
    } finally {
      setActionId(null);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t("employeeTabs.training")}
        </h3>
        {trainings.length === 0 ? (
          <p className="mt-3 text-sm text-gray-400">{t("common.noData")}</p>
        ) : (
          <div className="mt-3 space-y-2">
            {trainings.map((tr) => (
              <div
                key={tr.id}
                className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/50 p-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {tr.status === "completed" && (
                      <svg className="h-4 w-4 shrink-0 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    <p className="text-sm font-medium text-gray-900">
                      {tr.course_title ?? `Course #${tr.course_id}`}
                    </p>
                  </div>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Assigned: {DATE_FMT.format(new Date(tr.assigned_at))}
                    {tr.completed_at && (
                      <span className="ml-2">
                        Completed: {DATE_FMT.format(new Date(tr.completed_at))}
                      </span>
                    )}
                    {tr.expires_at && (
                      <span className="ml-2">
                        Expires: {DATE_FMT.format(new Date(tr.expires_at))}
                      </span>
                    )}
                    {tr.score != null && (
                      <span className="ml-2">Score: {tr.score}</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${trainingStatusTone(tr.status)}`}
                  >
                    {tr.status.replace(/_/g, " ")}
                  </span>
                  {tr.status === "not_started" && (
                    <button
                      type="button"
                      onClick={() => handleStart(tr.id)}
                      disabled={actionId === tr.id}
                      className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                    >
                      {actionId === tr.id ? "..." : "Start"}
                    </button>
                  )}
                  {tr.status === "in_progress" && (
                    <button
                      type="button"
                      onClick={() => handleComplete(tr.id)}
                      disabled={actionId === tr.id}
                      className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                    >
                      {actionId === tr.id ? "..." : "Complete"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Performance Tab (assets, equipment requisitions)
// ---------------------------------------------------------------------------

function PerformanceTab({ employeeId }: { employeeId: number }) {
  const { t } = useTranslation();
  const [assets, setAssets] = useState<EmployeeAsset[]>([]);
  const [requisitions, setRequisitions] = useState<EquipmentRequisition[]>([]);
  const [loading, setLoading] = useState(true);

  const [myCases, setMyCases] = useState<{ id: number; case_reference: string; subject: string; status: string; disciplinary_type: string; outcome: string | null; outcome_expiry_date: string | null; created_at: string | null; expired?: boolean }[]>([]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get<EmployeeAsset[]>(`/api/employees/${employeeId}/assets`),
      api.get<EquipmentRequisition[]>(
        `/api/employees/${employeeId}/requisitions`,
      ),
      api.get<typeof myCases>("/api/disciplinary/my-cases").catch(() => []),
    ])
      .then(([a, r, dc]) => {
        setAssets(a);
        setRequisitions(r);
        setMyCases(dc.filter((c: typeof myCases[0]) => !c.expired));
      })
      .catch(() => {
        /* silently fail */
      })
      .finally(() => setLoading(false));
  }, [employeeId]);

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Disciplinary */}
      {myCases.length > 0 && (
        <section className="rounded-xl border border-gray-200 bg-white p-6">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            {t("employeeDetails.disciplinary", "Disciplinary")}
          </h3>
          <div className="mt-3 space-y-2">
            {myCases.map((c) => (
              <div key={c.id} className="rounded-lg border border-gray-100 bg-gray-50/50 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{c.subject}</p>
                    <p className="text-xs text-gray-500">{c.disciplinary_type} &middot; {c.case_reference}</p>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${c.status === "closed" ? "bg-gray-100 text-gray-600" : "bg-red-50 text-red-700"}`}>
                    {c.status.replace(/_/g, " ")}
                  </span>
                </div>
                {c.outcome && (
                  <p className="mt-1 text-xs text-gray-500">{t("employeeDetails.outcome", "Outcome")}: {c.outcome.replace(/_/g, " ")}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Assets */}
      <section className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t("employeeDetails.assets")}
        </h3>
        {assets.length === 0 ? (
          <p className="mt-3 text-sm text-gray-400">{t("common.noData")}</p>
        ) : (
          <div className="mt-3 space-y-2">
            {assets.map((asset) => (
              <div
                key={asset.id}
                className="rounded-lg border border-gray-100 bg-gray-50/50 p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {asset.asset_description}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t(`assetCategories.${asset.asset_category}`, asset.asset_category)}
                    </p>
                    {asset.serial_number && (
                      <p className="text-[11px] text-gray-400">
                        S/N: {asset.serial_number}
                      </p>
                    )}
                  </div>
                  {asset.date_returned ? (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                      Returned
                    </span>
                  ) : (
                    <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                      On Loan
                    </span>
                  )}
                </div>
                {asset.date_loaned && (
                  <p className="mt-1 text-[11px] text-gray-400">
                    {t("employeeDetails.dateLoaned")}:{" "}
                    {DATE_FMT.format(new Date(asset.date_loaned))}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Equipment Requisitions */}
      <section className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t("employeeDetails.requisitions")}
        </h3>
        {requisitions.length === 0 ? (
          <p className="mt-3 text-sm text-gray-400">{t("common.noData")}</p>
        ) : (
          <div className="mt-3 space-y-2">
            {requisitions.map((req) => (
              <div
                key={req.id}
                className="rounded-lg border border-gray-100 bg-gray-50/50 p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {req.item_description}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t("employeeDetails.quantity")}: {req.quantity}
                      {req.estimated_budget &&
                        ` · ${t("employeeDetails.estimatedBudget")}: ${req.estimated_budget}`}
                    </p>
                  </div>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${requisitionStatusTone(req.status)}`}
                  >
                    {formatRequisitionStatus(req.status)}
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-gray-400">
                  {DATE_FMT.format(new Date(req.date_requested))}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Change Requests Section
// ---------------------------------------------------------------------------

function ChangeRequestsSection({
  changeRequests,
  changeRequestsOpen,
  onToggle,
  cancellingId,
  onCancel,
}: {
  changeRequests: ChangeRequest[];
  changeRequestsOpen: boolean;
  onToggle: () => void;
  cancellingId: number | null;
  onCancel: (id: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <section className="rounded-xl border border-gray-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between p-6"
      >
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t("selfService.myChangeRequests")}
        </h2>
        <svg
          className={`h-5 w-5 text-gray-400 transition-transform ${changeRequestsOpen ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {changeRequestsOpen && (
        <div className="space-y-3 px-6 pb-6">
          {changeRequests.map((cr) => (
            <div
              key={cr.id}
              className="rounded-lg border border-gray-100 bg-gray-50/50 p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {cr.field_label}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    <span className="text-gray-400">
                      {cr.current_value ?? "—"}
                    </span>
                    <span className="mx-1.5 text-gray-300">&rarr;</span>
                    <span className="font-medium text-gray-700">
                      {cr.requested_value}
                    </span>
                  </p>
                  {cr.reason && (
                    <p className="mt-1 text-xs italic text-gray-400">
                      {cr.reason}
                    </p>
                  )}
                  {cr.reviewer_notes && (
                    <p className="mt-1 text-xs text-gray-500">
                      Note: {cr.reviewer_notes}
                    </p>
                  )}
                  <p className="mt-1 text-[11px] text-gray-400">
                    {DATE_FMT.format(new Date(cr.created_at))}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${changeRequestStatusTone(cr.status)}`}
                  >
                    {t(`changeRequestStatuses.${cr.status}`, cr.status)}
                  </span>
                  {cr.status === "pending" && (
                    <button
                      type="button"
                      onClick={() => onCancel(cr.id)}
                      disabled={cancellingId === cr.id}
                      className="rounded-lg border border-gray-300 px-2 py-1 text-xs text-gray-600 disabled:opacity-60"
                    >
                      {cancellingId === cr.id
                        ? "..."
                        : t("common.cancel")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Active Surveys Section
// ---------------------------------------------------------------------------

interface SurveyConfig {
  id: number;
  survey_type: string;
  name: string;
  frequency: string;
  is_active: boolean;
  target_audience: string;
}

function ActiveSurveysSection() {
  const { t } = useTranslation();
  const [surveys, setSurveys] = useState<SurveyConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [respondedIds, setRespondedIds] = useState<Set<number>>(new Set());
  const [activeFormId, setActiveFormId] = useState<number | null>(null);
  const [score, setScore] = useState<number>(7);
  const [feedback, setFeedback] = useState("");
  const [happiness, setHappiness] = useState(3);
  const [motivation, setMotivation] = useState(3);
  const [companySupport, setCompanySupport] = useState(3);
  const [relationships, setRelationships] = useState(3);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<SurveyConfig[]>("/api/surveys/active")
      .then(setSurveys)
      .catch(() => setSurveys([]))
      .finally(() => setLoading(false));
  }, []);

  const resetForm = () => {
    setScore(7);
    setFeedback("");
    setHappiness(3);
    setMotivation(3);
    setCompanySupport(3);
    setRelationships(3);
    setSubmitError(null);
  };

  const handleSubmit = async (configId: number) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.post(`/api/surveys/${configId}/respond`, {
        score,
        feedback: feedback || null,
        wellbeing_happiness: happiness,
        wellbeing_motivation: motivation,
        wellbeing_company_support: companySupport,
        wellbeing_relationships: relationships,
      });
      setRespondedIds((prev) => new Set(prev).add(configId));
      setActiveFormId(null);
      resetForm();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setRespondedIds((prev) => new Set(prev).add(configId));
        setActiveFormId(null);
        resetForm();
      } else {
        setSubmitError(err instanceof ApiError ? err.message : t("common.error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const visibleSurveys = surveys.filter((s) => !respondedIds.has(s.id));

  if (loading || visibleSurveys.length === 0) return null;

  const wellbeingLabel = (value: number) => {
    switch (value) {
      case 1: return "Very Low";
      case 2: return "Low";
      case 3: return "Okay";
      case 4: return "Good";
      case 5: return "Excellent";
      default: return String(value);
    }
  };

  return (
    <section className="space-y-3">
      {visibleSurveys.map((survey) => (
        <div
          key={survey.id}
          className="rounded-xl border border-blue-200 bg-blue-50/50 p-5"
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">{survey.name}</p>
              <p className="text-xs text-gray-500">
                {survey.survey_type === "enps" ? "Employee Net Promoter Score" : survey.survey_type}
                {" · "}{survey.frequency}
              </p>
            </div>
            {activeFormId !== survey.id && (
              <button
                type="button"
                onClick={() => { resetForm(); setActiveFormId(survey.id); }}
                className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white"
              >
                Respond
              </button>
            )}
          </div>

          {activeFormId === survey.id && (
            <div className="mt-4 space-y-4">
              {/* eNPS Score */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  How likely are you to recommend this company as a place to work? (0-10)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={0}
                    max={10}
                    value={score}
                    onChange={(e) => setScore(Number(e.target.value))}
                    className="flex-1 accent-pme-blue"
                  />
                  <span className="w-8 text-center text-sm font-semibold text-gray-800">{score}</span>
                </div>
                <div className="mt-0.5 flex justify-between text-[10px] text-gray-400">
                  <span>Not likely</span>
                  <span>Very likely</span>
                </div>
              </div>

              {/* Feedback */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Feedback (optional)
                </label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  rows={2}
                  placeholder="Share your thoughts..."
                  className="input resize-none text-sm"
                />
              </div>

              {/* Wellbeing */}
              <div className="space-y-3">
                <p className="text-xs font-medium text-gray-700">Wellbeing</p>
                {([
                  { label: "Happiness", value: happiness, setter: setHappiness },
                  { label: "Motivation", value: motivation, setter: setMotivation },
                  { label: "Company Support", value: companySupport, setter: setCompanySupport },
                  { label: "Relationships", value: relationships, setter: setRelationships },
                ] as const).map(({ label, value, setter }) => (
                  <div key={label}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs text-gray-600">{label}</span>
                      <span className="text-[10px] text-gray-400">{wellbeingLabel(value)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="range"
                        min={1}
                        max={5}
                        value={value}
                        onChange={(e) => setter(Number(e.target.value))}
                        className="flex-1 accent-pme-blue"
                      />
                      <span className="w-5 text-center text-xs font-semibold text-gray-700">{value}</span>
                    </div>
                  </div>
                ))}
              </div>

              {submitError && (
                <p className="text-xs text-red-600">{submitError}</p>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => void handleSubmit(survey.id)}
                  disabled={submitting}
                  className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {submitting ? t("auth.saving") : "Submit Response"}
                </button>
                <button
                  type="button"
                  onClick={() => { setActiveFormId(null); resetForm(); }}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600"
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </section>
  );
}

// ---------------------------------------------------------------------------
// ProfileFieldRow
// ---------------------------------------------------------------------------

function ProfileFieldRow({
  field,
  editValue,
  onEditChange,
  isSaving,
  isSaved,
  error,
  isPending,
  isChangeRequestOpen,
  onOpenChangeRequest,
  onCloseChangeRequest,
  changeRequestValue,
  onChangeRequestValueChange,
  changeRequestReason,
  onChangeRequestReasonChange,
  changeRequestSubmitting,
  onSubmitChangeRequest,
}: {
  field: ProfileField;
  editValue: string;
  onEditChange: (val: string) => void;
  isSaving: boolean;
  isSaved: boolean;
  error: string | null;
  isPending: boolean;
  isChangeRequestOpen: boolean;
  onOpenChangeRequest: () => void;
  onCloseChangeRequest: () => void;
  changeRequestValue: string;
  onChangeRequestValueChange: (val: string) => void;
  changeRequestReason: string;
  onChangeRequestReasonChange: (val: string) => void;
  changeRequestSubmitting: boolean;
  onSubmitChangeRequest: () => void;
}) {
  const { t } = useTranslation();

  const displayValue = formatFieldValue(field.field_name, field.current_value);

  const Icon = fieldIcon(field.field_name, field.field_group);

  if (field.access_level === "view_only") {
    return (
      <div className="flex items-start gap-2.5">
        <Icon size={16} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-pme-blue/70" />
        <div className="min-w-0">
          <p className="text-xs font-medium text-pme-muted">{field.field_label}</p>
          <p className="mt-0.5 truncate text-sm font-medium text-pme-ink">
            {displayValue || <span className="font-normal text-gray-400">{t("profile.notSet")}</span>}
          </p>
        </div>
      </div>
    );
  }

  if (field.access_level === "direct_edit") {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-600">
          {field.field_label}
        </label>
        <input
          type="text"
          value={editValue}
          onChange={(e) => onEditChange(e.target.value)}
          disabled={isSaving}
          className="input mt-1"
        />
        {isSaved && (
          <p className="mt-1 text-xs text-green-600">{t("common.save")}d</p>
        )}
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </div>
    );
  }

  return (
    <div className={isChangeRequestOpen ? "sm:col-span-2 xl:col-span-3" : ""}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <label className="block text-xs font-medium text-gray-600">
            {field.field_label}
          </label>
          <p className="mt-0.5 text-sm text-gray-900">
            {displayValue || "—"}
          </p>
        </div>
        <div className="flex items-center gap-2 pt-1">
          {isPending && (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
              {t("selfService.pendingApproval")}
            </span>
          )}
          {!isPending && !isChangeRequestOpen && (
            <button
              type="button"
              onClick={onOpenChangeRequest}
              className="rounded-lg border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
            >
              {t("selfService.requestChange")}
            </button>
          )}
        </div>
      </div>

      {isChangeRequestOpen && (
        <div className="mt-2 space-y-2 rounded-lg border border-gray-200 bg-gray-50/50 p-3">
          <div>
            <label className="block text-xs font-medium text-gray-600">
              {t("selfService.newValue")}
            </label>
            <input
              type="text"
              value={changeRequestValue}
              onChange={(e) => onChangeRequestValueChange(e.target.value)}
              disabled={changeRequestSubmitting}
              className="input mt-1"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600">
              {t("selfService.reason")}
            </label>
            <textarea
              value={changeRequestReason}
              onChange={(e) => onChangeRequestReasonChange(e.target.value)}
              disabled={changeRequestSubmitting}
              rows={2}
              className="input mt-1 resize-none"
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onSubmitChangeRequest}
              disabled={changeRequestSubmitting || !changeRequestValue.trim()}
              className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
            >
              {changeRequestSubmitting
                ? t("auth.saving")
                : t("selfService.submitRequest")}
            </button>
            <button
              type="button"
              onClick={onCloseChangeRequest}
              disabled={changeRequestSubmitting}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-600"
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin PIN section
// ---------------------------------------------------------------------------

function AdminPinSection({ employee }: { employee: Me }) {
  const { t } = useTranslation();
  const hasPin = employee.has_admin_pin;
  const [showForm, setShowForm] = useState(false);
  const [currentAdminPin, setCurrentAdminPin] = useState("");
  const [newAdminPin, setNewAdminPin] = useState("");
  const [confirmAdminPin, setConfirmAdminPin] = useState("");
  const [adminPinError, setAdminPinError] = useState<string | null>(null);
  const [adminPinSuccess, setAdminPinSuccess] = useState<string | null>(null);
  const [adminPinSubmitting, setAdminPinSubmitting] = useState(false);

  const isValidLen = (p: string) => p.length === 4 || p.length === 6;

  const submitAdminPin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAdminPinError(null);
    setAdminPinSuccess(null);
    if (!isValidLen(newAdminPin)) {
      setAdminPinError(t("auth.adminPin.helper"));
      return;
    }
    if (newAdminPin !== confirmAdminPin) {
      setAdminPinError(t("auth.pinMismatch"));
      return;
    }
    setAdminPinSubmitting(true);
    try {
      if (hasPin) {
        await api.put("/api/auth/change-admin-pin", {
          current_pin: currentAdminPin,
          new_pin: newAdminPin,
        });
      } else {
        await api.post("/api/auth/set-initial-admin-pin", { pin: newAdminPin });
      }
      setAdminPinSuccess(t("auth.adminPin.updated"));
      setCurrentAdminPin("");
      setNewAdminPin("");
      setConfirmAdminPin("");
      setShowForm(false);
    } catch (err) {
      setAdminPinError(
        err instanceof ApiError ? err.message : t("common.error"),
      );
    } finally {
      setAdminPinSubmitting(false);
    }
  };

  return (
    <div className="mt-4 border-t border-gray-100 pt-4">
      <p className="text-xs font-medium text-gray-700">
        {t("auth.adminPin.label")}
      </p>
      {!showForm ? (
        <div className="mt-2 flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {hasPin ? t("common.set") : t("common.notSet")}
          </span>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs"
          >
            {hasPin
              ? t("auth.adminPin.changeAdminPin")
              : t("auth.adminPin.setAdminPin")}
          </button>
          {adminPinSuccess && (
            <span className="text-xs text-green-600">{adminPinSuccess}</span>
          )}
        </div>
      ) : (
        <form onSubmit={submitAdminPin} className="mt-2 space-y-2">
          {hasPin && (
            <PinField
              id="current_admin_pin"
              label={t("auth.adminPin.currentAdminPin")}
              value={currentAdminPin}
              onChange={setCurrentAdminPin}
              maxLen={6}
            />
          )}
          <PinField
            id="new_admin_pin"
            label={t("auth.adminPin.newAdminPin")}
            value={newAdminPin}
            onChange={setNewAdminPin}
            maxLen={6}
          />
          <PinField
            id="confirm_admin_pin"
            label={t("auth.adminPin.confirmAdminPin")}
            value={confirmAdminPin}
            onChange={setConfirmAdminPin}
            maxLen={6}
          />
          <p className="text-[11px] text-gray-400">
            {t("auth.adminPin.helper")}
          </p>
          {adminPinError && (
            <p className="text-xs text-red-600">{adminPinError}</p>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={adminPinSubmitting || !isValidLen(newAdminPin)}
              className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
            >
              {adminPinSubmitting
                ? t("auth.saving")
                : hasPin
                  ? t("auth.adminPin.update")
                  : t("auth.adminPin.setPin")}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setAdminPinError(null);
              }}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs"
            >
              {t("common.cancel")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PinField helper
// ---------------------------------------------------------------------------

function PinField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  maxLen = 12,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  maxLen?: number;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-gray-600">
        {label}
      </label>
      <input
        id={id}
        type="password"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete={autoComplete}
        maxLength={maxLen}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
        className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 text-base tracking-widest focus:border-pme-blue focus:outline-none focus:ring-2 focus:ring-pme-blue/30"
      />
    </div>
  );
}
